import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { MusicService } from 'fym-shared/src/music-services/types';
import { SerializedAccountForList } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { UserAvatar } from '../../common/views/userAvatar/UserAvatar';
import { SMLPageHead } from '../components/SMLPageHead/SMLPageHead';
import { useGetServiceConnectionsQuery } from '../../../redux/api/fymAPI';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { planNameMessages } from '../config/music24PlanNames';
import { smlNavigationMessages } from '../navigation/smlNavigationMessages';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { useMusic24Plan } from '../hooks/useMusic24Plan';
import { HOME_PERIOD_DAYS, homeFullPeriodPlan } from './homePeriod';
import { HomePeriod } from './useHomePeriod';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistHomeHeaderProps {
  account: SerializedAccountForList;
  period: HomePeriod;
}

/**
 * The artist header (design a1, b2, c1): avatar, name, the profiles read daily and the period.
 * The period chip is text, not a control. On Free the lock chip opens the upgrade prompt.
 * Below 768 px the period is a line under the name.
 */
const ArtistHomeHeaderComponent: React.FC<ArtistHomeHeaderProps> = ({ account, period }) => {
  const intl = useIntl();
  const openUpgradePrompt = useUpgradePrompt();
  const currentPlan = useMusic24Plan();
  const { data } = useGetServiceConnectionsQuery({ accountId: account.id });

  const readServices = useMemo(
    () =>
      (data?.connections ?? [])
        .filter(
          (connection) => (connection.artistProfileUrl ?? '').trim() !== '' && connection.scrapeSupported !== false
        )
        .map((connection) => musicServicesByMusicAPIID[connection.integrationType])
        .filter((service): service is MusicService => service !== undefined),
    [data]
  );

  const breadcrumbs = useMemo(() => [{ name: intl.formatMessage(smlNavigationMessages.home) }], [intl]);
  const openHistoryUpgrade = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.history), [openUpgradePrompt]);

  const meta = (
    <>
      {readServices.length > 0 && (
        <div className={styles.servicesLine}>
          {readServices.map((service) => (
            <React.Fragment key={service.id}>
              <span className={styles.dot} style={{ background: service.color.primary }} />
              {service.name}
            </React.Fragment>
          ))}
          <span className={styles.faint}>·</span>
          <span>{intl.formatMessage(messages.profilesRead, { count: readServices.length })}</span>
        </div>
      )}
      <div className={clsx(styles.periodLine, 'd-md-none')}>{period.label}</div>
    </>
  );

  return (
    <SMLPageHead
      breadcrumbs={breadcrumbs}
      title={account.name ?? ''}
      meta={meta}
      leading={<UserAvatar size={60} url={account.avatarProcessedUrl} name={account.name} />}
    >
      <div className={styles.headChips}>
        <span className={clsx(styles.period, 'd-none d-md-inline-flex')}>
          <FontAwesomeIcon icon={faCalendarDays} />
          {period.label}
        </span>
        {period.isShort && (
          <button type="button" className={styles.lockChip} onClick={openHistoryUpgrade}>
            <FontAwesomeIcon icon={faLock} />
            {intl.formatMessage(messages.periodLockDays, {
              currentPlan: intl.formatMessage(planNameMessages[currentPlan]),
              shortDays: period.days,
              plan: intl.formatMessage(planNameMessages[homeFullPeriodPlan()]),
              fullDays: HOME_PERIOD_DAYS,
            })}
          </button>
        )}
      </div>
    </SMLPageHead>
  );
};

export const ArtistHomeHeader = TypedMemo(ArtistHomeHeaderComponent);
